import { AxiosError } from "axios";
import { useQuery } from "react-query";
import { useErrorBoundary } from "component-library";
import { authAxios as axios } from "shared-auth";
import { HALManager, UserManager } from "core";
import { BasicInfo } from "models";
import { getApiUrl } from "services/UrlBuilder";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import { useGetPublicProfile } from "pages/Profile/hooks/useGetPublicProfile";

/** Shared with useGetBasicInfo — same React Query cache. */
export const GET_BASIC_INFO_QUERY_KEY = "GET_BASIC_INFO_QUERY_KEY";

/**
 * Raw basic info + public profile: public view uses {@link useGetPublicProfile};
 * private view uses the basic-info API. Keeps query keys in sync with useGetBasicInfo.
 */
export function useGetProfileBasicInfoSource() {
  const captureError = useErrorBoundary();
  const { isPublicView } = usePublicView();

  const { data: rawPublicProfile, isLoading: isPublicProfileLoading } =
    useGetPublicProfile();

  const { data: privateBasicInfo, isLoading: isPrivateBasicLoading } = useQuery<
    BasicInfo,
    AxiosError
  >(
    GET_BASIC_INFO_QUERY_KEY,
    async () => {
      const { data } = await axios.get(
        getApiUrl(HALManager.getBasicInfoUrl(UserManager.userId))
      );

      return data;
    },
    {
      enabled: !isPublicView,
      cacheTime: 0,
      onError: captureError,
    }
  );

  const rawBasicInfo = isPublicView
    ? rawPublicProfile?.basicInfo
    : privateBasicInfo;

  const isBasicInfoSourceLoading = isPublicView
    ? isPublicProfileLoading
    : isPrivateBasicLoading;

  return {
    isPublicView,
    rawPublicProfile,
    rawBasicInfo,
    isBasicInfoSourceLoading,
  };
}
